import React, { useState, useEffect } from "react";
import {
  ChakraProvider,
  Box,
  Card,
  AbsoluteCenter,
  Center,
  Image,
  FormLabel,
  Button,
  Flex,
  Text,
  Heading,
  VStack,
  Tag,
  HStack,
  SimpleGrid,
  Select,
  Grid,
  GridItem,
  Input,
  Textarea,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { Link, useParams, useLocation } from "react-router-dom";
import { ColorModeSwitcher } from "../../ColorModeSwitcher";
import { Nav } from "../../containers/Nav";
import { Footer } from "../../containers/Footer";
import { getBrand, updateBrand } from "../../actions/brands";
import { getBrandCampaigns } from "../../actions/campaigns";
import CampaignCard from "../../components/campaignCard";

import config from "../../config";

const root = config.REACT_APP_API_URL;
function BrandProfile() {
  const toast = useToast();
  const lightGray = useColorModeValue("gray.100", "gray.900");
  const params = useParams();
  const brand = params.brand;

  const search = useLocation().search;
  const setUp = Boolean(new URLSearchParams(search).get("setUp"));

  const [organization, setOrganization] = useState({});
  const [isOwner, setIsOwner] = useState(false);

  const [profileLogo, setProfileLogo] = useState(undefined);
  const [profileOrganization, setProfileOrganization] = useState("");
  const [profileLocation, setProfileLocation] = useState("");
  const [profileWebsite, setProfileWebsite] = useState("");
  const [profileMissionStatement, setProfileMissionStatement] = useState("");
  const [profileGenres, setProfileGenres] = useState([]);
  const [profileSocials, setProfileSocials] = useState([]);
  const [profileQueue, setProfileQueue] = useState([]);

  const [brandCampaigns, setBrandCampaigns] = useState([]);

  const [newSocial, setNewSocial] = useState("");

  const [canSaveChanges, setCanSaveChanges] = useState(true);

  const submitProfile = async () => {
    setCanSaveChanges(false);
    let updateBody = {};
    if (profileOrganization !== organization.organization) {
      updateBody.organization = profileOrganization;
    }
    if (profileLogo !== organization.logo) {
      updateBody.logo = profileLogo;
    }
    if (profileLocation !== organization.location) {
      updateBody.location = profileLocation;
    }
    if (profileWebsite !== organization.website) {
      updateBody.website = profileWebsite;
    }
    if (profileMissionStatement !== organization.missionStatement) {
      updateBody.missionStatement = profileMissionStatement;
    }
    if (
      profileGenres.length !== organization?.genres.length ||
      profileGenres.length === 0
    ) {
      updateBody.genres = profileGenres;
    }
    if (
      profileSocials.length !== organization?.socials?.length ||
      profileSocials.length === 0
    ) {
      updateBody.socials = profileSocials;
    }
    if (
      profileQueue.length !== organization?.queue?.length ||
      profileQueue.length === 0
    ) {
      updateBody.queue = profileQueue;
    }
    if (!Object.keys(updateBody).length) {
      return;
    }

    const response = await updateBrand(updateBody);
    if (response.ok) {
      toast({
        title: `Profile has been saved!`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    }
    setCanSaveChanges(true);
  };

  const submitCoverImage = (image) => {
    if (!image) return;
    if (image.size / 1000 / 1000 > 25) {
      toast({
        title: `Image can't be larger than 25mb!`,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
    setProfileLogo(image);
  };

  const addToQueueImage = (value) => {
    if (!value) return;
    setProfileQueue([...profileQueue, value]);
  };

  const removeQueueImage = (i) => {
    profileQueue.splice(i, 1);
    setProfileQueue([...profileQueue]);
  };

  useEffect(() => {
    if (brand && !organization._id) {
      getBrand(brand).then((org) => {
        setOrganization(org);

        getBrandCampaigns(brand).then((campaigns) => {
          setBrandCampaigns(campaigns.slice(0, 3));
        });

        if (localStorage.getItem("jwtToken")) {
          const rawJwt = localStorage.getItem("jwtToken").split("Bearer ")[1];
          const parsedJWT = JSON.parse(atob(rawJwt.split(".")[1]));

          if (parsedJWT.organization === org.organization) {
            setIsOwner(true);
          }
        }
        //setProfileLogo(org.logo);
        setProfileOrganization(org.organization);
        setProfileLocation(org.location);
        setProfileWebsite(org.website);
        setProfileMissionStatement(org.missionStatement);
        setProfileGenres(org.genres || []);
        setProfileSocials(org.socials || []);
        setProfileQueue(org.queue || []);
      });
    }
  }, []);

  const queueTaglines = [
    "What's out",
    "What's coming out",
    "What's in development",
  ];

  return (
    <Grid
      templateAreas={`"header header" "main main" "footer footer"`}
      gridTemplateRows={"50px 1fr 30px"}
      gridTemplateColumns={"150px 1fr"}
      pb="75px"
      color="blackAlpha.700"
    >
      <Nav />

      <GridItem pl="2" area={"main"}>
        <Center>
          <Box
            minWidth="980px"
            maxWidth="1200px"
            mx="5"
            mb="2"
            mt="8"
            justifyContent="left"
          >
            <Link to={brand ? "/brandCampaigns" : "/home"}>
              <Text as="u" color="teal">
                &lt;&lt; Go back home
              </Text>
            </Link>
          </Box>
        </Center>
        <Center>
          <Card
            minWidth="980px"
            maxWidth="1200px"
            mx="5"
            mb="5"
            p="3"
            position="relative"
            bg={useColorModeValue("gray.100", "gray.900")}
          >
            {isOwner && (
              <Box position="absolute" m="15px" top="0px" right="0px">
                <Button
                  isDisabled={!canSaveChanges}
                  onClick={submitProfile}
                  colorScheme="green"
                >
                  {canSaveChanges ? "Save changes" : "Loading..."}
                </Button>
              </Box>
            )}
            <VStack>
              <HStack justify="top" width="100%">
                <Box m="15" align="left" position="relative">
                  <Image
                    boxSize="250px"
                    objectFit="cover"
                    src={
                      profileLogo
                        ? URL.createObjectURL(profileLogo)
                        : "https://hubitprototype.herokuapp.com" +
                          organization.logo
                    }
                    fallbackSrc="https://placehold.co/250x250"
                    borderRadius="md"
                  />
                  {isOwner && (
                    <>
                      <Input
                        display="none"
                        position="absolute"
                        width="0px"
                        name="cover-image-upload"
                        id="cover-image-upload"
                        accept="image/png, image/jpeg"
                        type="file"
                        onChange={(e) => {
                          submitCoverImage(e.target.files[0]);
                        }}
                      ></Input>
                      <FormLabel
                        borderRadius="md"
                        border="0px"
                        cursor="pointer"
                        padding="12px"
                        outline="0"
                        htmlFor="cover-image-upload"
                        color="white"
                        fontSize="sm"
                        m="5px"
                        background="blue.500"
                        position="absolute"
                        right="0px"
                        bottom="0px"
                      >
                        <Text>edit</Text>
                      </FormLabel>
                    </>
                  )}
                </Box>
                <Box mx="25" align="left" px="15">
                  {isOwner && organization ? (
                    <Input
                      isDisabled={true}
                      variant="flushed"
                      fontSize="xl"
                      value={profileOrganization}
                      onChange={(e) => setProfileOrganization(e.target.value)}
                      placeholder={"organization name"}
                    />
                  ) : (
                    <Heading fontSize="2xl" as="b" mb="2">
                      {organization.organization}
                    </Heading>
                  )}

                  <Box fontSize="xl" align="left" mt="6">
                    <HStack>
                      <Text as="b">Location:</Text>
                      {isOwner ? (
                        <Input
                          variant="flushed"
                          fontSize="xl"
                          value={profileLocation}
                          onChange={(e) => setProfileLocation(e.target.value)}
                          placeholder={"Hollywood, CA"}
                        />
                      ) : (
                        <Text>{organization.location}</Text>
                      )}
                    </HStack>
                  </Box>

                  <Box fontSize="xl" align="left" mt="6">
                    <HStack>
                      <Text as="b">Website:</Text>
                      {isOwner ? (
                        <Input
                          variant="flushed"
                          fontSize="xl"
                          value={profileWebsite}
                          onChange={(e) => setProfileWebsite(e.target.value)}
                          placeholder={"www.your-website.com"}
                        />
                      ) : (
                        <Link
                          to={`https://${organization.website}`}
                          target="_blank"
                        >
                          <Text color="blue.500" as="u">
                            {organization.website}
                          </Text>
                        </Link>
                      )}
                    </HStack>
                  </Box>
                </Box>
              </HStack>

              <Box width="100%" mt="5" mx="25" align="left" px="15">
                <Flex>
                  <Text
                    as="b"
                    width="30%"
                    justify="top"
                    fontSize="xl"
                    align="left"
                  >
                    Mission Statement:
                  </Text>
                  {isOwner ? (
                    <Textarea
                      width="100%"
                      fontSize="lg"
                      mx="6"
                      value={profileMissionStatement}
                      onChange={(e) => {
                        if (e.target.value.length <= 100) {
                          setProfileMissionStatement(e.target.value);
                        }
                      }}
                      placeholder="At company-name, we provide high-quality content to global audiences across various platforms."
                    />
                  ) : (
                    <Text width="100%" fontSize="lg" align="left" mx="6">
                      {organization.missionStatement}
                    </Text>
                  )}
                </Flex>
              </Box>

              <Box width="100%" m="25" align="left" px="15">
                <HStack>
                  <VStack width="45%" align="left">
                    <Text as="b" justify="top" fontSize="xl">
                      Types of content we make:
                    </Text>
                    {isOwner && organization && (
                      <Text justify="top" fontSize="sm">
                        (choose up to 3)
                      </Text>
                    )}
                  </VStack>
                  {isOwner && organization ? (
                    <HStack width="100%" spacing={4} align="left">
                      {profileGenres.length > 0 &&
                        profileGenres.map((genre, idx) => {
                          return (
                            <Tag
                              size={"lg"}
                              key={genre}
                              variant="solid"
                              colorScheme="purple"
                            >
                              {genre}
                              <Button
                                minWidth="25px"
                                height="25px"
                                color="white"
                                bg={"purple.600"}
                                variant="ghost"
                                p="2px"
                                fontSize="15px"
                                ml="2"
                                mr="0"
                                _hover={{ bg: "purple.900" }}
                                onClick={(e) => {
                                  profileGenres.splice(idx, 1);
                                  setProfileGenres([...profileGenres]);
                                }}
                              >
                                x
                              </Button>
                            </Tag>
                          );
                        })}
                      <Box>
                        {profileGenres.length < 3 && (
                          <Select
                            onChange={(e) => {
                              const uppercaseGenre =
                                e.target.value.charAt(0).toUpperCase() +
                                e.target.value.slice(1, e.target.value.length);

                              if (profileGenres.includes(uppercaseGenre)) {
                                return;
                              }

                              setProfileGenres([
                                ...profileGenres,
                                uppercaseGenre,
                              ]);
                            }}
                            maxWidth="155px"
                            placeholder="Select genre"
                            value=""
                          >
                            <option value="romance">Romance</option>
                            <option value="comedy">Comedy</option>
                            <option value="drama">Drama</option>
                            <option value="fantasy">Fantasy</option>
                            <option value="thriller/horror">
                              Thriller/Horror
                            </option>
                          </Select>
                        )}
                      </Box>
                    </HStack>
                  ) : (
                    <HStack width="100%" spacing={4} align="left">
                      {organization.genres &&
                        organization.genres.map((genre) => {
                          return (
                            <Tag
                              py="6px"
                              size={"lg"}
                              key={genre}
                              variant="solid"
                              colorScheme="purple"
                            >
                              {genre}
                            </Tag>
                          );
                        })}
                    </HStack>
                  )}
                </HStack>
              </Box>
              <Box width="100%" m="25" align="left" px="15">
                <HStack>
                  <VStack width="30%" align="left">
                    <Text as="b" justify="top" fontSize="xl">
                      Production Queue:
                    </Text>
                    {isOwner && organization && (
                      <Text>
                        Tell us about your slate! What’s currently out? What’s
                        coming out? What’s in development?
                      </Text>
                    )}
                    {isOwner && organization && (
                      <Text justify="top" fontSize="sm">
                        (choose up to 3)
                      </Text>
                    )}
                  </VStack>
                  {isOwner && organization ? (
                    <HStack width="100%" spacing={4} align="left">
                      {profileQueue.map((queue, idx) => {
                        return (
                          <Box position="relative">
                            <Image
                              borderWidth="2px"
                              borderRadius="xl"
                              boxSize="200"
                              objectFit="cover"
                              src={
                                typeof queue === "object" && queue
                                  ? URL.createObjectURL(queue)
                                  : "https://hubitprototype.herokuapp.com" +
                                    queue
                              }
                            />
                            <Button
                              position="absolute"
                              top="4px"
                              right="4px"
                              fontSize="lg"
                              colorScheme="red"
                              onClick={() => {
                                removeQueueImage(idx);
                              }}
                            >
                              -
                            </Button>
                            <Center>
                              <Text mt="1" as="b">
                                {queueTaglines[idx]}
                              </Text>
                            </Center>
                          </Box>
                        );
                      })}

                      <Box>
                        {profileQueue.length < 3 && (
                          <Box>
                            <Input
                              display="none"
                              position="absolute"
                              width="0px"
                              name="queue-image-upload"
                              id="queue-image-upload"
                              accept="image/png, image/jpeg"
                              type="file"
                              onChange={(e) => {
                                addToQueueImage(e.target.files[0]);
                              }}
                            ></Input>
                            <FormLabel
                              cursor="pointer"
                              htmlFor="queue-image-upload"
                              position="relative"
                              fontSize="xl"
                              width="200px"
                              height="200px"
                              background="blue.100"
                              borderStyle="dashed"
                              borderColor="blue.200"
                              borderWidth="2px"
                              borderRadius="xl"
                              justifyContent="center"
                            >
                              <AbsoluteCenter>
                                <Text
                                  color="blue.300"
                                  lineHeight="30px"
                                  as="span"
                                  fontSize="55px"
                                >
                                  +
                                </Text>
                              </AbsoluteCenter>
                            </FormLabel>
                            <Center>
                              <Text mt="0" as="b">
                                {queueTaglines[profileQueue.length]}
                              </Text>
                            </Center>
                          </Box>
                        )}
                      </Box>
                    </HStack>
                  ) : (
                    <HStack width="100%" spacing={4} align="left">
                      {profileQueue &&
                        profileQueue.map((queue, idx) => {
                          return (
                            <VStack>
                              <Image
                                borderWidth="2px"
                                borderRadius="xl"
                                boxSize="200"
                                objectFit="cover"
                                src={
                                  typeof queue === "object"
                                    ? URL.createObjectURL(queue)
                                    : "https://hubitprototype.herokuapp.com" +
                                      queue
                                }
                              />
                              <Center>
                                <Text mt="0" as="b">
                                  {queueTaglines[idx]}
                                </Text>
                              </Center>
                            </VStack>
                          );
                        })}
                    </HStack>
                  )}
                </HStack>
              </Box>
              <Box width="100%" m="25" align="left" px="15">
                <HStack>
                  <VStack width="30%" align="left">
                    <Text as="b" justify="top" fontSize="xl">
                      Social Links:
                    </Text>
                    {isOwner && organization && (
                      <Text justify="top" fontSize="sm">
                        (choose up to 3)
                      </Text>
                    )}
                  </VStack>
                  {isOwner ? (
                    <HStack width="100%" spacing={4} align="left">
                      {profileSocials.map((social, tdx) => {
                        return (
                          <Tag
                            px="12px"
                            py="8px"
                            size={"lg"}
                            key={social}
                            variant="outline"
                            colorScheme="blue"
                          >
                            <Link to={`https://${social}`} target="_blank">
                              <Text as="u">{social}</Text>
                            </Link>
                            <Button
                              minWidth="25px"
                              height="25px"
                              color="white"
                              bg={"blue.600"}
                              variant="ghost"
                              p="2px"
                              fontSize="15px"
                              ml="2"
                              mr="0"
                              _hover={{ bg: "blue.900" }}
                              onClick={(e) => {
                                profileSocials.splice(tdx, 1);
                                setProfileSocials([...profileSocials]);
                              }}
                            >
                              x
                            </Button>
                          </Tag>
                        );
                      })}
                      {profileSocials.length < 2 && (
                        <Box align="left" justify="left">
                          <Input
                            width="250px"
                            onChange={(e) => {
                              setNewSocial(e.target.value);
                            }}
                            value={newSocial}
                            type="text"
                            placeholder="Enter social media url"
                          />
                          <Button
                            onClick={() => {
                              setNewSocial("");
                              setProfileSocials([...profileSocials, newSocial]);
                            }}
                            fontSize="lg"
                            mx="2"
                            colorScheme="teal"
                          >
                            +
                          </Button>
                        </Box>
                      )}
                    </HStack>
                  ) : (
                    <HStack width="100%" justify="left">
                      {organization.socials?.map((social, tdx) => {
                        return (
                          <Tag
                            px="12px"
                            py="8px"
                            size={"lg"}
                            key={social}
                            variant="outline"
                            colorScheme="blue"
                          >
                            <Link to={`https://${social}`} target="_blank">
                              <Text as="u">{social}</Text>
                            </Link>
                          </Tag>
                        );
                      })}
                    </HStack>
                  )}
                </HStack>
              </Box>
            </VStack>
          </Card>
        </Center>
        {brandCampaigns.length > 0 && (
          <Center>
            <Card
              minWidth="980px"
              maxWidth="1200px"
              mx="5"
              mb="5"
              p="3"
              position="relative"
              bg={lightGray}
            >
              <Center>
                <Heading fontSize="2xl" mt="4" mb="1">
                  Latest Campaigns
                </Heading>
              </Center>
              <Box mt="5">
                <SimpleGrid columns={[2, null, 3]}>
                  {brandCampaigns.map((campaign, idx) => {
                    return <CampaignCard campaign={campaign} owner={isOwner} />;
                  })}
                </SimpleGrid>
              </Box>
            </Card>
          </Center>
        )}

        {setUp && (
          <Center>
            <Link to="/campaigns">
              <Button colorScheme="blue">Continue to homepage</Button>
            </Link>
          </Center>
        )}
      </GridItem>

      <Footer />
    </Grid>
  );
}

export default BrandProfile;
